import ArticleLayout from '../../components/ArticleLayout';
import VentilstiftFigure from '../../components/figures/VentilstiftFigure';

export default function RadiatorVentilSidderFast() {
  return (
    <ArticleLayout
      title="Ventilstiften sidder fast efter sommeren: Sådan motionerer du den"
      metaTitle="Radiator ventil sidder fast? Sådan løsner du stiften"
      metaDescription="Er radiatoren iskold efter sommeren? Lær at tage termostathovedet af og motionere den lille ventilstift fri med en flad genstand uden vandspild."
      canonicalPath="/radiator-ventil-sidder-fast"
      introLead="Når efterårskulden sætter ind, og radiatoren forbliver iskold trods fuld opvarmning på termostaten, er årsagen i 9 ud af 10 tilfælde en fastgroet ventilstift."
      introAnswer="I løbet af sommeren har termostaten presset den lille metalstift i bund for at holde radiatoren lukket. Når stiften står i bund i flere måneder, gror den fast i kalk og mikroskopiske jernpartikler. Løsningen er at afmontere termostathovedet og massere stiften fri med en flad genstand."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Sådan løsner du ventilstiften' },
        { id: 'hvorfor-gror-fast', title: '2. Hvorfor gror stiften fast i sommervarmen?' },
        { id: 'fejl-traekke-ud', title: '3. Advarsel: Træk aldrig i stiften med en tang' },
        { id: 'forebyggelse', title: '4. Sådan undgår du problemet næste år' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: 5 trin til at frigøre en fastgroet ventil"
      checklist={[
        {
          title: 'Drej termostathovedet til maksimal åbning (trin 5)',
          description: 'Derved aflastes det indvendige spændbånd, så termostathovedet let kan tages af ventilhalsen.',
        },
        {
          title: 'Afmonter termostathovedet fra ventilhuset',
          description: 'Drej snapkraven mod uret eller løsn unbrakoskruen, og træk det hvide plasthoved lige bagud.',
        },
        {
          title: 'Lokaliser den lille metalstift midt i ventilens messinghus',
          description: 'Stiften er ca. 2 mm tyk og stikker normalt ca. 3–4 mm ud fra den lille pakdåse.',
        },
        {
          title: 'Tryk stiften ind med en flad, hård genstand (fx bagsiden af en teske)',
          description: 'Tryk stiften 2–3 mm ind og giv slip. Gentag bevægelsen roligt 10–15 gange, indtil den fjedrer frit ud af sig selv.',
        },
        {
          title: 'Genmonter termostathovedet, og mærk røret blive lunt efter 5 minutter',
          description: 'Sæt hovedet på igen, spænd kraven, og stil termostaten på den ønskede temperatur (typisk trin 3).',
        },
      ]}
      diagram={<VentilstiftFigure />}
      sections={[
        {
          id: 'hvorfor-gror-fast',
          title: 'Hvorfor gror ventilstiften fast hvert eneste efterår?',
          content: (
            <>
              <p>
                Bag den lille stift inde i radiatorventilen sidder en stærk returfjeder og en gummikegle, der regulerer vandstrømmen.
              </p>
              <p>
                Når termostaten skrues ned på 0 eller frostsikring i maj måned, trykker termostatbælgen stiften hårdt ind i pakdåsen. Når stiften forbliver ubevægelig i den samme position gennem 4–5 varme måneder, aflejres kalk og mikroskopiske magnetit-partikler fra vandet omkring stiften. Fjederen indeni er ikke stærk nok til at bryde denne mikroskopiske kalkhinde af sig selv, når du igen skruer op i oktober.
              </p>
            </>
          ),
        },
        {
          id: 'fejl-traekke-ud',
          title: 'Advarsel: Træk ALDRIG i ventilstiften med en tang',
          content: (
            <>
              <p>
                En klassisk og farlig fejl blandt boligejere er at gribe fat om stiften med en spidstang eller bidetang og hive hårdt udad.
              </p>
              <p>
                <strong>Hvorfor du aldrig må trække i stiften:</strong> Stiften holdes kun på plads af en lille låsering i pakdåsen. Hvis du river stiften ud, åbnes der direkte ind til anlæggets varme vand, som fosser ud i rummet. Desuden vil riller fra en tang ridse metallet, så pakdåsen efterfølgende bliver utæt og begynder at dryppe.
              </p>
              <p>
                Stiften skal altid presses <em>indad</em> med en flad genstand. Det er ventilens egen indre fjederkraft, der skal skubbe den ud igen. Er stiften meget genstridig, kan du give siden af ventilens massive messinghus et par lette bank med en lille hammer eller skruetrækker for at vibrere kalken løs.
              </p>
            </>
          ),
        },
        {
          id: 'forebyggelse',
          title: 'Sådan forhindrer du fastgroede ventiler næste sommer',
          content: (
            <>
              <p>
                Du kan spare dig selv for meget besvær ved at følge forsyningsselskabernes enkle sommerråd:
              </p>
              <p>
                Lad alle radiatortermostater i boligen stå fuldt åbne (trin 5) hen over sommeren, forudsat at dit varmeanlæg eller fjernvarmeunit har en automatisk sommerlukning eller du lukker for sommerventilen i kælderen. Når termostaten står på 5, er fjederen helt aflastet, og stiften står i sin naturlige, frie yderposition og kan ikke gro fast i bundstillingen.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal pakdåsen eller ventilen skiftes?',
        content: (
          <p>
            Hvis stiften ikke vil give sig overhovedet, eller hvis der pibler vand ud omkring stiften, er pakdåsen slidt op. På moderne Danfoss-ventiler kan pakdåsen skrues ud og udskiftes med en ny (Danfoss servicenummer 013G0290) uden at tømme vand af anlægget, da ventilen har indbygget tætning. Hvis du er usikker, bestilles en autoriseret VVS-installatør.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Løber der vand ud på gulvet, når jeg tager termostathovedet af?',
          a: 'Nej, overhovedet ikke. Termostathovedet er en tør udvendig del. Selve vandet er fuldstændig forseglet inde i messingventilens pakdåse bag stiften.',
        },
        {
          q: 'Hvor langt skal stiften kunne bevæge sig?',
          a: 'Stiften har en vandring på blot ca. 2–3 millimeter. Den skal glide jævnt ind, når du trykker på den, og springe kontant tilbage af sig selv, når du slipper.',
        },
        {
          q: 'Må man smøre stiften med olie?',
          a: 'En lille dråbe syrefri symaskineolie eller silikoneolie på stiften kan smøre pakningen. Brug aldrig fedtede madolier eller aggressive opløsningsmidler, som kan nedbryde gummipakningerne.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'den overordnede tjekliste' },
        { to: '/radiator-termostat', text: 'Radiatortermostater forklaret', desc: 'sådan fungerer termostaten og følerelementet' },
        { to: '/radiator-laekker', text: 'Radiatoren lækker', desc: 'udskiftning af pakdåse ved vanddryp' },
        { to: '/spar-paa-varmen', text: 'Spar på varmen', desc: 'sommerlukning og korrekt drift' },
        { to: '/radiator-varmer-selvom-den-er-slukket', text: 'Radiatoren varmer, selvom den er slukket', desc: 'det modsatte problem' },
      ]}
      sources={[
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/sommerluk-varmeanlaegget', label: 'Sommerluk varmeanlægget og spar 500 kr. på 5 min | SparEnergi' },
        { url: 'https://www.danfoss.com/da-dk/industries/buildings-residential/dhs/solutions-for-homeowners/saving-energy/', label: 'Gode råd til at spare på varmen | Danfoss' },
      ]}
    />
  );
}
